import { useLayoutEffect, useState, type KeyboardEvent } from 'react';
import { InputBase, Paper, Popper } from '@mui/material';
import { useGridApiContext, type GridRenderEditCellParams } from '@mui/x-data-grid-premium';

/** A multiline editor floating under the cell, like the entity forms' Notes field. */
export default function NotesEditCell({ id, field, value, colDef, hasFocus }: GridRenderEditCellParams) {
  const apiRef = useGridApiContext();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [input, setInput] = useState<HTMLTextAreaElement | null>(null);
  useLayoutEffect(() => { if (hasFocus) input?.focus(); }, [hasFocus, input]);

  // The popper is portalled but React still bubbles its keys to the grid, which would commit on any Enter.
  const onKeyDown = (e: KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === 'Tab') apiRef.current.stopCellEditMode({ id, field });
    else if (e.key === 'Escape') apiRef.current.stopCellEditMode({ id, field, ignoreModifications: true });
    else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      apiRef.current.stopCellEditMode({ id, field });
    }
  };

  return (
    <div ref={setAnchor} style={{ alignSelf: 'flex-start', width: colDef.computedWidth, height: 1 }}>
      {anchor && (
        <Popper open anchorEl={anchor} placement="bottom-start" style={{ zIndex: 1300 }}>
          {/* Else the row's context menu replaces the browser's paste and spellcheck menu. */}
          <Paper elevation={3} sx={{ p: 1, width: Math.max(colDef.computedWidth, 280) }} onContextMenu={(e) => e.stopPropagation()}>
            <InputBase
              multiline minRows={3} maxRows={10} fullWidth value={value ?? ''}
              inputRef={setInput}
              onChange={(e) => apiRef.current.setEditCellValue({ id, field, value: e.target.value })}
              onKeyDown={onKeyDown}
            />
          </Paper>
        </Popper>
      )}
    </div>
  );
}
